"use client"
import React, { useEffect, useState } from 'react'
import { ICONS, IMAGES } from '@/constants/constants'
import Image from 'next/image'
import PublicExpandableCard from '@/components/PublicExpandableCard'
import { getFAQs, FAQCategory } from '@/lib/api/faq'
import { LoadingButton } from '@/components/atomic'
import { Phone, Info } from 'lucide-react'


const Faq = () => {
  // Client-side fetch to avoid inconsistencies
  const [faqData, setFaqData] = useState<FAQCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  // Handle click outside and escape key to close tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showTooltip && !target.closest('.tooltip-container')) {
        setShowTooltip(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showTooltip) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showTooltip]);

  useEffect(() => {
    let mounted = true;

    const fetchFAQs = async () => {
      try {
        setLoading(true);
        const response = await getFAQs();
        if (!mounted) return;
        if (response && response.success && response.data) {
          setFaqData(response.data.faq || []);
        } else {
          setError('Failed to fetch FAQs');
        }
      } catch (err) {
        setError('An error occurred while fetching FAQs');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchFAQs();

    return () => {
      mounted = false;
    };
  }, []);

  const faqCategories = [
    {
      id: 1,
      icon: ICONS.cash,
      title: 'Payment & Pricing',
      description: 'Information about fares, payment methods, and refund policies'
    },
    {
      id: 2,
      icon: ICONS.cancellation,
      title: 'Cancellations',
      description: 'Learn about our cancellation policy and refund process'
    },
    {
      id: 3,
      icon: ICONS.ferryIcon,
      title: 'Ferry Services',
      description: 'General information about our ferry operations'
    }
  ]

  return (
    <div className='pt-10 font-work-sans'>
      <div className='flex justify-center items-center flex-col gap-5'>
        <h1 className='font-rakesly uppercase font-bold text-4xl max-md:text-2xl'>Frequently Asked Questions</h1>
        <p className='text-gray-500 font-normal max-lg:text-center'>Find answers to common questions about our ferry services, booking process, and policies.</p>
      </div>


      <div>

        <Image src={IMAGES.wave} alt='wave' height={1000} width={1000} className='w-full mb-[-100px] max-lg:hidden' />
        <div className='flex justify-center mt-12 bg-white  py-20 max-md:py-5'>

          <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 max-md:pr-5 max-md:pl-5 gap-6 max-w-6xl'>
            {faqCategories.map((category) => (
              <div key={category.id} className='bg-white border gap-2 flex flex-col border-gray-200 rounded-lg p-4'>
                <div className='flex justify-start max-md:hidden'>

                  <Image
                    src={category.icon}
                    alt={category.title}
                    width={32}
                    height={32}
                  />
                </div>
                <div className='flex gap-2'>
                  <Image
                    src={category.icon}
                    alt={category.title}
                    width={20}
                    height={20}
                    className='max-md:block hidden'
                  /> <h3 className='font-normal '>{category.title}</h3>
                </div>
                <p className='text-gray-600 text-sm '>{category.description}</p>
              </div>
            ))}
          </div>
        </div>

        <Image src={IMAGES.wave} alt='wave' height={1000} width={1000} className='w-full mt-[-20px] max-sm:mt-[-10px] transform rotate-180' />

      </div>

      {/* FAQ Expandable Section */}
      <div className='py-20'>
        <div className='max-w-4xl mx-auto px-4'>
          {loading ? (
            <div className="text-center text-gray-500">
              <p className="text-lg font-medium">Loading FAQs...</p>
            </div>
          ) : error ? (
            <div className="text-center text-red-500 bg-red-50 p-4 rounded-lg max-w-md mx-auto">
              <p className="font-medium">Error loading FAQs</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          ) : faqData.length > 0 ? (
            faqData.map((category) => (
              <PublicExpandableCard key={category.id} category={category} />
            ))
          ) : (
            <div className="text-center text-gray-500">
              <p className="text-lg font-medium">No FAQs available</p>
              <p className="text-sm mt-2">Please check back later for frequently asked questions.</p>
            </div>
          )}
        </div>
      </div>

      <div className=''>
        <Image src={IMAGES.wave} alt='wave' height={1000} width={1000} className='w-full mb-[-50px] max-lg:mb-[-30px] max-md:mb-[-10px]' />
        <div className='flex gap-5 justify-center items-center flex-col bg-white py-20 px-5'>
          <h1 className='font-semibold text-2xl'>Still have questions?</h1>
          <p className='text-gray-500 text-center font-normal max-lg:text-center max-w-2xl'>All inquiries will be responded to within 1–2 business days. For urgent matters, please contact the ferry line directly at <span className='font-bold'>250-625-2445</span>. Response times may vary during peak seasons, holidays, or unforeseen operational circumstances.</p>
          <div className='flex flex-col items-center gap-3'>
            <div className='flex items-center max-md:flex-col justify-center gap-3 max-md:gap-2'>
              <a href="tel:250.625.3293" className='flex-shrink-0'>
                <LoadingButton className='font-semibold py-3 px-4 max-md:px-3 max-md:text-sm'>
                  <Phone height={15} width={15} className='max-md:hidden' />
                  Contact Support
                </LoadingButton>
              </a>
              <div className='relative flex items-center justify-center flex-shrink-0 tooltip-container'>
                <Info
                  height={20}
                  width={20}
                  className='text-gray-500 cursor-pointer hover:text-gray-700 transition-colors max-md:h-4 max-md:w-4'
                  onClick={() => setShowTooltip(!showTooltip)}
                  onMouseEnter={() => {
                    // Only use hover on non-touch devices
                    if (!('ontouchstart' in window)) {
                      setShowTooltip(true)
                    }
                  }}
                  onMouseLeave={() => {
                    // Only use hover on non-touch devices
                    if (!('ontouchstart' in window)) {
                      setShowTooltip(false)
                    }
                  }}
                />
                {showTooltip && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg transition-all duration-200 whitespace-nowrap max-md:whitespace-normal max-md:w-60 max-md:text-center z-50 shadow-lg">
                    Our phone support is available from 9:00 AM to 5:00 PM (PDT), Monday to Saturday
                    <div className='absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800'></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}

export default Faq